import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import MultiSelectAutocompleteNew from 'src/components-new/inputs/MultiSelectAutocompleteNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import PatientLookupDialog from 'src/components-new/data/PatientLookupDialog'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

export interface EchoRequestFormValues {
  title: string
  // One request is created per selected exam type (the backend only accepts a single
  // `examType` string per request) — see handleSaveRequest in EchocardiographicRequestsListNew.
  examTypes: string[]
  description: string
  patient: Record<string, any>
}

// Same value/label pair as the legacy page (src/pages/EchocardiographicRequests/EchocardiographicRequests.tsx) —
// additional exam types exist only as mock data (mock-server/psp-backend/exams-requests/get.js),
// not hardcoded here.
export const EXAM_TYPE_OPTIONS = [
  {value: 'eco', label: 'ECO'},
]

function getExamTypeLabel(examType: string): string {
  return EXAM_TYPE_OPTIONS.find(opt => opt.value === examType)?.label ?? examType
}

interface EchocardiographicRequestDialogNewProps {
  open: boolean
  onClose: () => void
  onSave: (values: EchoRequestFormValues) => void | Promise<void>
  readOnly?: boolean
  // In readOnly mode `patient` is already a display string (stringified by the list page,
  // same as the legacy page's fetchData), not the raw patient object.
  initialValues?: Record<string, any>
}

/**
 * Single dialog used both to create an echocardiographic exam request and, in `readOnly`
 * mode, to show the read-only detail of an existing one — new-style equivalent of
 * src/components/templates/EchocardiographicRequestDialog/EchocardiographicRequestDialog.tsx.
 * Modeled on RequestSampleDialogNew.tsx for the patient-lookup part, with the extra
 * title/examType/description fields from the legacy dialog.
 */
export default function EchocardiographicRequestDialogNew({
  open,
  onClose,
  onSave,
  readOnly = false,
  initialValues,
}: EchocardiographicRequestDialogNewProps): JSX.Element {
  const [title, setTitle] = useState('')
  // Array even in readOnly mode, where an existing request only ever carries one
  // examType — normalized to a single-item array in the effect below so the same
  // rendering/label logic works for both create and detail views.
  const [examTypes, setExamTypes] = useState<string[]>(['eco'])
  const [description, setDescription] = useState('')
  const [patientName, setPatientName] = useState('')
  const [currentPatient, setCurrentPatient] = useState<Record<string, any> | undefined>()
  const [lookupOpen, setLookupOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setTitle(initialValues?.title ?? '')
      setExamTypes(initialValues?.examType ? [initialValues.examType] : ['eco'])
      setDescription(initialValues?.description ?? '')
      setPatientName(typeof initialValues?.patient === 'string' ? initialValues.patient : '')
      setCurrentPatient(undefined)
      setLookupOpen(false)
      setSaving(false)
    }
  }, [open, initialValues])

  const isValid = Boolean(title.trim()) && examTypes.length > 0 && Boolean(currentPatient)

  const handleConfirm = async () => {
    if (!isValid || saving) return
    setSaving(true)
    try {
      await onSave({title: title.trim(), examTypes, description, patient: currentPatient!})
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppDialog
        actions={
          readOnly ? (
            <Button onClick={onClose} variant="secondary">Chiudi</Button>
          ) : (
            <>
              <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
              <Button disabled={!isValid} loading={saving} onClick={handleConfirm} variant="primary">
                Continua
              </Button>
            </>
          )
        }
        maxWidth="md"
        onClose={onClose}
        open={open}
        title={readOnly ? 'Dettaglio richiesta' : 'Richiedi esame'}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
          {readOnly ? (
            <ReadOnlyField label="Titolo" value={title} />
          ) : (
            <TextFieldNew fullWidth label="Titolo" onChange={setTitle} required value={title} />
          )}

          {readOnly ? (
            <ReadOnlyField label="Tipo esame" value={examTypes.map(getExamTypeLabel).join(', ')} />
          ) : (
            <MultiSelectAutocompleteNew
              fullWidth
              label="Tipo esame"
              onChange={setExamTypes}
              options={EXAM_TYPE_OPTIONS}
              required
              value={examTypes}
            />
          )}

          {readOnly ? (
            <ReadOnlyField label="Descrizione" value={description} />
          ) : (
            <TextAreaNew fullWidth label="Descrizione" onChange={setDescription} value={description} />
          )}

          {readOnly ? (
            <ReadOnlyField label="Paziente" value={patientName} />
          ) : (
            <Box>
              <Typography
                sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
                variant="caption"
              >
                Paziente
              </Typography>
              {currentPatient ? (
                <Box sx={{mt: 0.5}}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '8px',
                      backgroundColor: NEUTRAL_SCALE[50],
                      border: `1px solid ${NEUTRAL_SCALE[200]}`,
                    }}
                  >
                    <Typography variant="body2">{patientName}</Typography>
                    <ActionIconButton
                      onClick={() => {
                        setCurrentPatient(undefined)
                        setPatientName('')
                      }}
                      tooltip="Rimuovi"
                      variant="close"
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
                  <Button onClick={() => setLookupOpen(true)} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
                    Aggiungi Paziente
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </AppDialog>

      {lookupOpen && (
        <PatientLookupDialog
          onClose={() => setLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentPatient(row)
            setPatientName(`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim())
            setLookupOpen(false)
          }}
        />
      )}
    </>
  )
}